import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    Search,
    Filter,
    Plus,
    MoreVertical,
    Eye,
    Edit2,
    Ban,
    FileText,
    Phone,
    Mail,
    Calendar,
    AlertCircle,
    CheckCircle2,
    Clock,
    ArrowUpRight,
    Loader2,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { formatPrice } from "../../utils/productUtils";
import AdminCustomerService from "../../services/admin/AdminCustomerService";
import { CustomersListSkeleton } from "../../components/admin/customers/CustomersSkeleton";
import { useNotification } from "../../context/NotificationContext";
import { useComingSoon } from "../../utils/useComingSoon";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

export default function CustomersPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { showComingSoon } = useComingSoon();
    const { checkPermission } = useAdminPermissions();

    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isStatsLoading, setIsStatsLoading] = useState(true);
    const [statsData, setStatsData] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

    const statusConfig = {
        active: { label: "Active", color: "text-green-600 bg-green-50 border-green-100", icon: CheckCircle2 },
        overdue: { label: "Overdue", color: "text-red-600 bg-red-50 border-red-100", icon: AlertCircle },
        inactive: { label: "Inactive", color: "text-gray-500 bg-gray-50 border-gray-200", icon: Clock },
        blocked: { label: "Blocked", color: "text-red-700 bg-red-100 border-red-200", icon: Ban },
    };

    const fetchStats = async () => {
        setIsStatsLoading(true);
        try {
            const result = await AdminCustomerService.getStats();
            if (result.status === "success") {
                setStatsData(result.data);
            }
        } catch (error) {
            console.error("Failed to fetch stats:", error);
        } finally {
            setIsStatsLoading(false);
        }
    };

    const fetchCustomers = async () => {
        setIsLoading(true);
        try {
            const result = await AdminCustomerService.getAll({
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm || undefined,
                status: statusFilter === "all" ? undefined : statusFilter
            });
            if (result.status === "success") {
                setCustomers(result.data);
                setPagination(result.pagination);
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Error",
                message: "Failed to load customers."
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchCustomers();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, statusFilter, pagination.page]);

    const handleToggleBlock = async (id) => {
        if (!checkPermission("admin", "block or unblock customers")) return;
        setUpdatingId(id);
        try {
            const result = await AdminCustomerService.toggleBlock(id);
            if (result.status === "success") {
                showNotification({
                    type: "success",
                    title: "Status Updated",
                    message: result.message
                });
                await fetchCustomers();
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Action Failed",
                message: error.response?.data?.message || "Failed to toggle block status."
            });
        } finally {
            setUpdatingId(null);
        }
    };

    const stats = useMemo(() => [
        { label: "Total Customers", value: statsData?.total_customers || 0, icon: Users, color: "text-blue-600 bg-blue-50" },
        { label: "Active Rentals", value: statsData?.active_rentals_count || 0, icon: ArrowUpRight, color: "text-green-600 bg-green-50" },
        { label: "Overdue Follow-up", value: statsData?.overdue_count || 0, icon: AlertCircle, color: "text-red-600 bg-red-50" },
        { label: "Total Revenue", value: `₦${formatPrice(statsData?.total_revenue || 0)}`, icon: FileText, color: "text-purple-600 bg-purple-50" },
    ], [statsData]);

    return (
        <AdminLayout>
            <div className="p-4 lg:p-8 max-w-[1600px] mx-auto space-y-8">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                            Customers
                        </h1>
                        <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0]">
                            Manage and track rental customers
                        </p>
                    </div>
                    <button
                        onClick={() => showComingSoon({ title: "Add Customer Coming Soon!", message: "We're building the Add Customer feature. It will be available in the next update. Stay tuned!" })}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-[#62397f] hover:bg-[#7C3AED] text-white rounded-2xl font-bold transition-all shadow-lg shadow-purple-200 dark:shadow-none active:scale-95"
                    >
                        <Plus size={20} />
                        <span>Add Customer</span>
                    </button>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {isStatsLoading ? (
                        [...Array(4)].map((_, idx) => (
                            <div key={idx} className="bg-white dark:bg-[#1A1A1A] p-6 rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm animate-pulse">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-2xl bg-gray-100 dark:bg-[#262626] w-12 h-12" />
                                    <div className="space-y-2">
                                        <div className="h-3 w-20 bg-gray-100 dark:bg-[#262626] rounded-full" />
                                        <div className="h-5 w-16 bg-gray-100 dark:bg-[#262626] rounded-full" />
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        stats.map((stat, idx) => (
                            <div key={idx} className="bg-white dark:bg-[#1A1A1A] p-6 rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-2xl ${stat.color}`}>
                                        <stat.icon size={24} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider">{stat.label}</p>
                                        <p className="text-xl lg:text-sm font-bold text-[#111111] dark:text-white">{stat.value}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Filters */}
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by name, phone or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:ring-2 focus:ring-[#62397f] transition-all outline-none dark:text-white"
                        />
                    </div>
                    <div className="flex gap-2">
                        <div className="relative">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="appearance-none pl-10 pr-10 py-3 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm font-medium focus:ring-2 focus:ring-[#62397f] transition-all outline-none dark:text-white cursor-pointer"
                            >
                                <option value="all">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="overdue">Overdue</option>
                                <option value="inactive">Inactive</option>
                                <option value="blocked">Blocked</option>
                            </select>
                            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        </div>
                    </div>
                </div>

                {/* Customer Table */}
                {isLoading ? (
                    <CustomersListSkeleton />
                ) : (
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50/50 dark:bg-[#212121] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                                        <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider min-w-[280px]">Customer</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Rentals</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider text-right">Revenue</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Last Activity</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#E5E7EB] dark:divide-[#2A2A2A]">
                                    {customers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-20 text-center">
                                                <div className="flex flex-col items-center gap-3 text-gray-400">
                                                    <Users size={48} strokeWidth={1} />
                                                    <p className="text-sm">No customers found matching your criteria</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        customers.map((customer) => {
                                            const StatusIcon = statusConfig[customer.status].icon;
                                            return (
                                                <tr key={customer.id} className="group hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-[#62397f] font-bold text-sm">
                                                                {customer.name.split(' ').map(n => n[0]).join('')}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-bold text-[#111111] dark:text-white group-hover:text-[#62397f] transition-colors cursor-pointer" onClick={() => navigate(`/admin/customers/${customer.id}`)}>
                                                                    {customer.name}
                                                                </p>
                                                                <div className="flex items-center gap-3 mt-1">
                                                                    <span className="flex items-center gap-1 text-[11px] text-gray-400">
                                                                        <Phone size={10} /> {customer.phone}
                                                                    </span>
                                                                    {customer.email && (
                                                                        <span className="flex items-center gap-1 text-[11px] text-gray-400">
                                                                            <Mail size={10} /> {customer.email}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-bold text-[#111111] dark:text-white">{customer.total_rentals}</span>
                                                            {customer.active_rentals > 0 && (
                                                                <span className="text-[10px] text-green-500 font-bold flex items-center gap-1">
                                                                    <ArrowUpRight size={10} /> {customer.active_rentals} Active
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className="text-sm font-bold text-[#111111] dark:text-white">₦{formatPrice(customer.total_revenue)}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2 text-sm text-[#4B5563] dark:text-[#A0A0A0]">
                                                            <Calendar size={14} />
                                                            {customer.last_rental_date || "No activity"}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusConfig[customer.status].color}`}>
                                                            <StatusIcon size={12} />
                                                            {statusConfig[customer.status].label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button
                                                                onClick={() => navigate(`/admin/customers/${customer.id}`)}
                                                                className="p-2 text-[#6B7280] dark:text-[#A0A0A0] hover:bg-gray-100 dark:hover:bg-[#333333] rounded-xl transition-colors"
                                                                title="View Detail"
                                                            >
                                                                <Eye size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    if (checkPermission("admin", "edit customer details")) {
                                                                        navigate(`/admin/customers/${customer.id}/edit`);
                                                                    }
                                                                }}
                                                                className="p-2 text-[#6B7280] dark:text-[#A0A0A0] hover:bg-gray-100 dark:hover:bg-[#333333] rounded-xl transition-colors"
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleToggleBlock(customer.id)}
                                                                disabled={updatingId === customer.id}
                                                                className={`p-2 rounded-xl transition-colors disabled:opacity-50 disabled:scale-100 ${customer.status === 'blocked' ? 'text-green-500 hover:bg-green-50' : 'text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10'}`}
                                                                title={customer.status === 'blocked' ? 'Unblock' : 'Block'}
                                                            >
                                                                {updatingId === customer.id ? (
                                                                    <Loader2 size={18} className="animate-spin" />
                                                                ) : (
                                                                    <Ban size={18} />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
